import { useTaskTranslation } from './useTaskTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { authorizeClient } from '../../../networking/client';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectLocationId } from '../../../store/slices/auth';
import {
  fetchCustomers,
  selectCustomers,
  selectCustomersStatus,
  ICustomer,
} from '../../../store/slices/customers';
import type { TaskCustomerData } from '../../../store/slices/tasks';

export const hasValidCustomerId = (customer: ICustomer) =>
  customer.person_external_id != null &&
  String(customer.person_external_id).trim() !== '' &&
  Number.isFinite(Number(customer.person_external_id));

export const useTaskCustomerSelection = () => {
  const { translations } = useTaskTranslation();
  const dispatch = useAppDispatch();
  const customers = useAppSelector(selectCustomers);
  const customersStatus = useAppSelector(selectCustomersStatus);
  const locationId = useAppSelector(selectLocationId);
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detailsPending, setDetailsPending] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsRetry, setDetailsRetry] = useState(0);
  const detailsRequest = useRef<{
    customerId: number;
    promise: Promise<{ language: string; error?: string }>;
  } | null>(null);

  const options = useMemo(
    () =>
      customers.map((customer, index) => ({
        customer,
        // Preserve distinct source rows when IDs are missing or repeated.
        key: `${customer.person_id ?? 'customer'}-${index}`,
      })),
    [customers]
  );
  const selectedCustomer = options.find(({ key }) => key === selectedKey)?.customer;
  const selectedCustomerId =
    selectedCustomer && hasValidCustomerId(selectedCustomer)
      ? Number(selectedCustomer.person_external_id)
      : null;
  const selectedLanguage = selectedCustomer?.language?.trim();

  useEffect(() => {
    setSelectedKey(null);
  }, [locationId]);

  useEffect(() => {
    if (locationId && customers.length === 0) dispatch(fetchCustomers());
  }, [dispatch, locationId, customers.length]);

  useEffect(() => {
    detailsRequest.current = null;
    setDetailsError('');
    setDetailsPending(false);
    if (selectedCustomerId == null || selectedLanguage) return;
    let active = true;
    const controller = new AbortController();
    setDetailsPending(true);
    const promise = authorizeClient
      .get<{ language?: string }>(`/ident/person/${selectedCustomerId}/settings`, {
        signal: controller.signal,
        timeout: 10000,
      })
      .then((response) => {
        const language = response.data.language?.trim();
        if (!language) throw new Error('task_board.customer_language_is_unavailable');
        return { language };
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error && error.message === 'task_board.customer_language_is_unavailable'
            ? error.message
            : 'task_board.unable_to_load_customer_details_please_retry';
        if (active) setDetailsError(message);
        return { language: '', error: message };
      })
      .finally(() => {
        if (active) setDetailsPending(false);
      });
    detailsRequest.current = { customerId: selectedCustomerId, promise };
    return () => {
      active = false;
      controller.abort();
      detailsRequest.current = null;
    };
  }, [selectedCustomerId, selectedLanguage, detailsRetry]);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return options.filter(
      ({ customer }) =>
        !query ||
        [
          `${customer.person_firstname || ''} ${customer.person_lastname || ''}`,
          customer.person_email,
          customer.person_mobile_phone,
          customer.person_external_id,
        ].some((value) => value != null && String(value).toLowerCase().includes(query))
    );
  }, [options, search]);

  // Both forms use the same validation and socket/REST customer schema.
  // Await the prefetched request so Edit can enable Save immediately on selection.
  const getCustomerPayload = async (): Promise<TaskCustomerData | null> => {
    if (selectedKey == null) return null;
    if (!selectedCustomer || customersStatus !== 'idle')
      throw new Error(
        translations('task_board.wait_for_the_customer_list_to_load_then_select_the_customer_again')
      );
    if (selectedCustomerId == null)
      throw new Error(translations('task_board.select_a_customer_with_a_valid_customer_id'));
    let language = selectedLanguage;
    if (!language) {
      const request = detailsRequest.current;
      if (!request || request.customerId !== selectedCustomerId)
        throw new Error(translations('task_board.wait_for_the_selected_customer_details_to_load'));
      const result = await request.promise;
      if (detailsRequest.current !== request)
        throw new Error(
          translations('task_board.customer_selection_changed_please_select_the_customer_again')
        );
      if (!result.language)
        throw new Error(
          translations(result.error || 'task_board.unable_to_load_customer_details_please_retry')
        );
      language = result.language;
    }
    return {
      externalId: selectedCustomerId,
      roles: selectedCustomer.roles ?? [],
      emailAddress: selectedCustomer.person_email ?? '',
      language,
      name: [selectedCustomer.person_firstname, selectedCustomer.person_lastname]
        .filter(Boolean)
        .join(' '),
      givenName: selectedCustomer.person_firstname ?? '',
    };
  };

  return {
    search,
    setSearch,
    selectedKey,
    selectedCustomer,
    selectCustomer: setSelectedKey,
    clearSelection: () => setSelectedKey(null),
    hasSelection: selectedKey != null,
    filteredOptions,
    hasCustomers: customers.length > 0,
    customersPending: !locationId || customersStatus === 'pending',
    customersReady: Boolean(locationId) && customersStatus === 'idle',
    customersError: Boolean(locationId) && customersStatus === 'rejected',
    retryCustomers: () => dispatch(fetchCustomers()),
    detailsPending,
    detailsError: detailsError ? translations(detailsError) : '',
    retryDetails: () => setDetailsRetry((value) => value + 1),
    getCustomerPayload,
  };
};

export type TaskCustomerSelection = ReturnType<typeof useTaskCustomerSelection>;
